import { useState, useMemo } from 'react';
import { Search, Briefcase, CheckCircle2, MessageSquare, ArrowUpDown, Filter } from 'lucide-react';
import { useCreatorPortal } from '../../contexts/CreatorPortalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatEuro } from '../../utils/currency';
import { CreatorCampaignCard } from './CreatorCampaignCard';
import type { CreatorCampaignData } from '../../services/creatorPortalService';

type ViewFilter = 'active' | 'completed' | 'consultation';
type ActiveSort = 'status' | 'date';
type ActiveFilter = 'all' | 'inProgress' | 'billing';

const STATUS_ORDER: Record<string, number> = {
  'in progress': 0,
  'production': 0,
  'em andamento': 0,
  'billing': 1,
  'faturação': 1,
};

function getStatusRank(status: string): number {
  return STATUS_ORDER[status.toLowerCase()] ?? 2;
}

function sortByStatus(a: CreatorCampaignData, b: CreatorCampaignData): number {
  const rankDiff = getStatusRank(a.status) - getStatusRank(b.status);
  if (rankDiff !== 0) return rankDiff;
  const dateA = a.updated_at || '';
  const dateB = b.updated_at || '';
  return dateB.localeCompare(dateA);
}

function sortByDate(a: CreatorCampaignData, b: CreatorCampaignData): number {
  const dateA = a.company_invoice_issued_date || a.updated_at || '';
  const dateB = b.company_invoice_issued_date || b.updated_at || '';
  return dateB.localeCompare(dateA);
}

function sortCompletedByPaymentDate(a: CreatorCampaignData, b: CreatorCampaignData): number {
  const dateA = a.last_payment_date || a.updated_at || '';
  const dateB = b.last_payment_date || b.updated_at || '';
  return dateB.localeCompare(dateA);
}

function matchesActiveFilter(c: CreatorCampaignData, filter: ActiveFilter): boolean {
  if (filter === 'all') return true;
  const s = c.status.toLowerCase();
  if (filter === 'inProgress') return s === 'in progress' || s === 'production' || s === 'em andamento';
  if (filter === 'billing') return s === 'billing' || s === 'faturação';
  return true;
}

function searchFilter(c: CreatorCampaignData, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return c.name.toLowerCase().includes(q) || c.brand.toLowerCase().includes(q);
}

interface ColumnProps {
  title: string;
  campaigns: CreatorCampaignData[];
  icon: React.ElementType;
  accentColor: string;
  badgeBg: string;
  emptyMessage: string;
  searchQuery: string;
  borderStyle?: string;
  bgStyle?: string;
  compact?: boolean;
  toolbar?: React.ReactNode;
}

function CampaignColumn({
  title, campaigns, icon: Icon, accentColor, badgeBg,
  emptyMessage, searchQuery, borderStyle, bgStyle, compact, toolbar
}: ColumnProps) {
  const filtered = campaigns.filter(c => searchFilter(c, searchQuery));
  const totalValue = filtered.reduce((s, c) => s + c.net_earnings, 0);

  return (
    <div className={`flex flex-col rounded-xl border ${borderStyle || 'border-gray-200'} ${bgStyle || 'bg-white'} overflow-hidden`}>
      <div className="px-4 py-3 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${accentColor}`} />
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${badgeBg}`}>
              {filtered.length}
            </span>
          </div>
          <span className="text-xs font-medium text-gray-500">{formatEuro(totalValue)}</span>
        </div>
        {toolbar && <div className="mt-2">{toolbar}</div>}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[calc(100vh-380px)] min-h-[200px]">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">{emptyMessage}</p>
          </div>
        ) : (
          filtered.map(c => (
            <CreatorCampaignCard key={c.id} campaign={c} compact={compact} />
          ))
        )}
      </div>
    </div>
  );
}

function ActiveToolbar({
  sortMode, onSortChange, filterMode, onFilterChange
}: {
  sortMode: ActiveSort;
  onSortChange: (s: ActiveSort) => void;
  filterMode: ActiveFilter;
  onFilterChange: (f: ActiveFilter) => void;
}) {
  const { t } = useLanguage();

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={() => onSortChange(sortMode === 'status' ? 'date' : 'status')}
        className="flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 px-2 py-1 rounded-xl transition-colors"
      >
        <ArrowUpDown className="w-3 h-3" />
        {sortMode === 'status' ? t('creatorsHub.sortByStatus') : t('creatorsHub.sortByDate')}
      </button>
      <div className="flex items-center gap-0.5 bg-gray-50 rounded-xl p-0.5">
        <Filter className="w-3 h-3 text-gray-400 ml-1" />
        {(['all', 'inProgress', 'billing'] as ActiveFilter[]).map(f => (
          <button
            key={f}
            onClick={() => onFilterChange(f)}
            className={`text-[11px] font-medium px-2 py-0.5 rounded transition-colors ${
              filterMode === f
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {f === 'all' ? t('creatorsHub.filterAll')
              : f === 'inProgress' ? t('creatorsHub.filterInProgress')
              : t('creatorsHub.filterBilling')}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CreatorCampaignBoard() {
  const { t } = useLanguage();
  const { activeCampaigns, completedCampaigns, consultationCampaigns, campaigns } = useCreatorPortal();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileTab, setMobileTab] = useState<ViewFilter>('active');
  const [activeSort, setActiveSort] = useState<ActiveSort>('status');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');

  const sortedActiveCampaigns = useMemo(() => {
    const filtered = activeCampaigns.filter(c => matchesActiveFilter(c, activeFilter));
    return [...filtered].sort(activeSort === 'status' ? sortByStatus : sortByDate);
  }, [activeCampaigns, activeSort, activeFilter]);

  const sortedCompletedCampaigns = useMemo(() => {
    return [...completedCampaigns].sort(sortCompletedByPaymentDate);
  }, [completedCampaigns]);

  const mobileTabCampaigns = mobileTab === 'completed' ? sortedCompletedCampaigns
    : mobileTab === 'consultation' ? consultationCampaigns
    : sortedActiveCampaigns;

  const mobileTabs: { key: ViewFilter; label: string; count: number; color: string }[] = [
    { key: 'active', label: t('creatorsHub.active'), count: sortedActiveCampaigns.length, color: 'bg-emerald-500' },
    { key: 'completed', label: t('creatorsHub.completed'), count: sortedCompletedCampaigns.length, color: 'bg-green-500' },
    { key: 'consultation', label: t('creatorsHub.consultation'), count: consultationCampaigns.length, color: 'bg-gray-500' },
  ];

  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 border border-gray-100 p-12 text-center">
        <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">{t('creatorsHub.noCampaignsAssigned')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder={t('creatorsHub.searchPlaceholder')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
        />
      </div>

      {/* Desktop: three columns */}
      <div className="hidden lg:grid lg:grid-cols-[2fr_1fr_1fr] gap-4">
        <CampaignColumn
          title={t('creatorsHub.activeCampaigns')}
          campaigns={sortedActiveCampaigns}
          icon={Briefcase}
          accentColor="text-blue-600"
          badgeBg="bg-blue-100 text-blue-700"
          emptyMessage={t('creatorsHub.noActiveCampaigns')}
          searchQuery={searchQuery}
          toolbar={
            <ActiveToolbar
              sortMode={activeSort}
              onSortChange={setActiveSort}
              filterMode={activeFilter}
              onFilterChange={setActiveFilter}
            />
          }
        />
        <CampaignColumn
          title={t('creatorsHub.completedCampaigns')}
          campaigns={sortedCompletedCampaigns}
          icon={CheckCircle2}
          accentColor="text-green-600"
          badgeBg="bg-green-100 text-green-700"
          emptyMessage={t('creatorsHub.noCompletedCampaigns')}
          searchQuery={searchQuery}
          bgStyle="bg-gray-50/50"
          compact
        />
        <CampaignColumn
          title={t('creatorsHub.consultationCampaigns')}
          campaigns={consultationCampaigns}
          icon={MessageSquare}
          accentColor="text-gray-500"
          badgeBg="bg-gray-100 text-gray-600"
          emptyMessage={t('creatorsHub.noConsultationCampaigns')}
          searchQuery={searchQuery}
          borderStyle="border-dashed border-gray-300"
          compact
        />
      </div>

      {/* Mobile / Tablet: tabs */}
      <div className="lg:hidden space-y-4">
        <div className="flex items-center gap-1.5 bg-white p-1.5 rounded-xl border border-gray-200">
          {mobileTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setMobileTab(tab.key)}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                mobileTab === tab.key
                  ? `${tab.color} text-white shadow-sm`
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                mobileTab === tab.key ? 'bg-white/25' : 'bg-gray-100'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {mobileTab === 'active' && (
          <ActiveToolbar
            sortMode={activeSort}
            onSortChange={setActiveSort}
            filterMode={activeFilter}
            onFilterChange={setActiveFilter}
          />
        )}

        <div className="space-y-3">
          {mobileTabCampaigns
            .filter(c => searchFilter(c, searchQuery))
            .map(c => (
              <CreatorCampaignCard key={c.id} campaign={c} />
            ))
          }
          {mobileTabCampaigns.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 border border-gray-100 p-8 text-center">
              <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">
                {mobileTab === 'active' ? t('creatorsHub.noActiveCampaigns')
                  : mobileTab === 'completed' ? t('creatorsHub.noCompletedCampaigns')
                  : t('creatorsHub.noConsultationCampaigns')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
